import React from 'react';
import { Integration, IntegrationRoomRefreshResult } from '@app/model/integration-settings/Integration';
import FormButton from '@ui/button/FormButton';
import { formatDayTime, TYPE_LABEL } from '../IntegrationSettings.utilities';

const IntegrationRoomCatalogue: React.FunctionComponent<{
    integration: Integration,
    isBusy: boolean,
    isRefreshing: boolean,
    isSharedRefreshing?: boolean,
    isDirty: boolean,
    result: IntegrationRoomRefreshResult | undefined,
    error: string | null,
    onRefresh: () => void,
}> = ({ integration, isBusy, isRefreshing, isSharedRefreshing = false, isDirty, result, error, onRefresh }) => {
    const catalogue = integration.catalogue;
    const saved = integration.status !== 'NOT_SET_UP';
    const fetched = catalogue.syncedAt !== null;
    const requirement = integration.type === 'PRICING' ? 'active rate plans' : integration.type === 'RESERVATIONS' ? 'room types and rooms' : 'room types';
    return <section aria-label={`${TYPE_LABEL[integration.type]} room catalogue`} className="mt-6 rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm">
                <p className="font-semibold text-gray-900">Room catalogue · {catalogue.ready ? 'Ready' : 'Not ready'}</p>
                <p className="mt-1 text-gray-600">{catalogue.roomTypes} room types · {catalogue.ratePlans} rate plans · {catalogue.rooms} rooms</p>
                <p className="mt-1 text-gray-500">{fetched ? `Last sync ${formatDayTime(catalogue.syncedAt!)}` : 'No completed fetch yet'}</p>
            </div>
            <FormButton state="white" disabled={!saved || isBusy || catalogue.busy || isDirty} onClick={onRefresh}>
                {isRefreshing ? 'Fetching rooms…' : isSharedRefreshing ? 'Shared catalogue refresh…' : fetched ? 'Refresh rooms' : 'Fetch rooms'}
            </FormButton>
        </div>
        <p className="mt-3 text-sm text-gray-500">
            {!saved ? 'Save the connection first, then fetch its room catalogue.' : isDirty ? 'Save your changes before fetching. Fetch uses the saved connection.'
                : (catalogue.busy || isSharedRefreshing) && !isRefreshing ? 'Another room catalogue refresh is running. Retry after it finishes.'
                : !catalogue.ready ? `Fetch ${requirement} before switching this integration on.` : 'Shared by this property’s integrations using the same provider.'}
        </p>
        {isSharedRefreshing && <p role="status" className="mt-2 text-sm text-gray-600">Another integration is refreshing this property’s shared provider catalogue.</p>}
        {isRefreshing && <p role="status" className="mt-2 text-sm text-gray-600">Fetching the saved provider catalogue. This can take a few minutes.</p>}
        {(error !== null || result !== undefined) && <p role={error !== null || result?.result === 'FAILED' ? 'alert' : 'status'}
            className={`mt-3 break-words text-sm ${error !== null || result?.result === 'FAILED' ? 'text-red-700' : 'text-gray-700'}`}>
            {error ?? result?.message}{result?.retryAt ? ` Retry after ${formatDayTime(result.retryAt)}.` : ''}
        </p>}
    </section>;
};

export default IntegrationRoomCatalogue;
